import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import { useMemo, useCallback } from "react"
import { toast } from "react-toastify"
import { Form, FormFeedback, Label, Button } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import {
  useCreateCustomerArea,
  useEditCustomerArea,
} from "hooks/useCustomerArea"

const CustomerAreaAddEdit = ({ modal, toggleModal, isEdit, editedData }) => {
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateCustomerArea()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditCustomerArea()

  // Formik validation
  const validationSchema = Yup.object({
    name: Yup.mixed().required("Please enter area name"),
    description: Yup.mixed(),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        ...values,
        name: values.name?.value || values.name,
        description: values.description?.value || values.description,
      }

      const action = isEdit
        ? editMutation({ id: editedData.id, ...payload })
        : createMutation(payload)

      try {
        const res = await action
        toast.success(
          res?.message ??
            `Area ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
      } catch (error) {
        console.error(error)
        toast.error("Failed to save Area.")
      }
    },
    [isEdit, editedData, editMutation, createMutation, toggleModal]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editedData?.name || "",
      description: editedData?.description || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  // Memoize button label and state
  const buttonText = useMemo(() => {
    const loading = editLoading || createLoading
    return `${isEdit ? "Save Changes" : "Add Area"}${
      loading ? "..." : ""
    }`
  }, [isEdit, editLoading, createLoading])

  const isSubmitDisabled = useMemo(
    () => editLoading || createLoading,
    [editLoading, createLoading]
  )

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Area" : "Add Area"}
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        <div className="mb-3">
          <Label htmlFor="remarks">Name</Label>
          <InputField
            type="text"
            name="name"
            id="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            invalid={formik.touched.name && !!formik.errors.name}
          />
          {formik.touched.name && formik.errors.name && (
            <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
          )}
        </div>

        <div className="mb-3">
          <Label htmlFor="remarks">Description</Label>
          <InputField
            type="text"
            name="description"
            id="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            invalid={formik.touched.description && !!formik.errors.description}
          />
          {formik.touched.description && formik.errors.description && (
            <FormFeedback type="invalid">{formik.errors.description}</FormFeedback>
          )}
        </div>

        <div className="text-end">
          <Button color="success" type="submit" disabled={isSubmitDisabled}>
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default CustomerAreaAddEdit
